import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, CardFooter, Col, Row } from 'reactstrap';
import { useDispatch } from 'react-redux';
import * as Icon from 'react-feather';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import Papa from 'papaparse';

import CallexLoader from '../../../components/CallexLoader';
import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';
import Pagination from '../../../components/form/Pagination';
import AdminHeader from '../../../components/header/AdminHeader';
import FormDateInput from '../../../components/form/FormDateInput';
import AiChatTranscriptModal from './AiChatTranscriptModal';
import useReportDownload from '../../../hooks/useReportDownload';
import { checkModulePermission, formatTableDateTime } from '../../../utils/common';

import { useUserRolePermission } from '../../../store/slices/authSlice';
import { useTimezone } from '../../../store/slices/commonSlice';
import { callexAiAgentOptionsGet } from '../../../store/slices/tenantSlices/configurationSlice';
import { aiDispositionListGet } from '../../../store/slices/tenantSlices/settingSlice';
import {
  botChatReportExport,
  botChatReportGet
} from '../../../store/slices/tenantSlices/dailyReportSlice';

const PERMISSION_KEY = 'AI Chat Report';
const EXPORT_NAME = 'AI Chat';

const CHAT_STATUS_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Open', value: 'open' },
  { label: 'Closed', value: 'closed' }
];

const CLOSE_REASON_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Inactivity', value: 'inactivity' },
  { label: 'Ended', value: 'ended' },
  { label: 'Manual', value: 'manual' }
];

const capitalize = (value) => String(value).charAt(0).toUpperCase() + String(value).slice(1);

const COLUMNS = [
  { name: 'started_at', label: 'Started At', render: formatTableDateTime },
  { name: 'bot_name', label: 'Bot' },
  { name: 'channel', label: 'Channel' },
  { name: 'status', label: 'Status', render: capitalize },
  { name: 'message_count', label: 'Messages' },
  { name: 'disposition_name', label: 'Disposition' },
  { name: 'close_reason', label: 'Close Reason', render: capitalize },
  { name: 'customer_name', label: 'Customer' },
  { name: 'ended_at', label: 'Ended At', render: formatTableDateTime }
];

// 0 is a real value here (message_count), so it must not fall back to '-'.
const renderCell = (value, render) => {
  if (value === null || value === undefined || value === '') return '-';
  return render ? render(value) : String(value);
};

// The export CSV spells the close reason out ("Closed manually") where the listing shows
// the API's short value, so map it back by keyword and keep any other text as it is.
const CLOSE_REASON_KEYWORDS = [
  ['manual', 'Manual'],
  ['inactiv', 'Inactivity'],
  ['end', 'Ended']
];
const toCloseReasonLabel = (text) =>
  CLOSE_REASON_KEYWORDS.find(([keyword]) => text.toLowerCase().includes(keyword))?.[1] || text;

// Listing columns as the backend CSV names them. The CSV has no channel, so that one
// listing column is left out.
const EXPORT_COLUMNS = [
  { csv: 'Started', label: 'Started At', render: formatTableDateTime },
  { csv: 'Bot', label: 'Bot' },
  { csv: 'Status', label: 'Status', render: capitalize },
  { csv: 'Messages', label: 'Messages' },
  { csv: 'Disposition', label: 'Disposition' },
  { csv: 'Close reason', label: 'Close Reason', render: toCloseReasonLabel },
  { csv: 'Customer', label: 'Customer' },
  { csv: 'Ended', label: 'Ended At', render: formatTableDateTime }
];

// Byte order mark: lets Excel read Hindi and other non-English text in the CSV correctly.
const BOM = String.fromCharCode(0xfeff);

// The backend CSV carries an ID column and ISO UTC times; reshape it to the listing's
// columns and formats before it is saved.
const toListingCsv = async (blob) => {
  const text = await blob.text();
  const { data } = Papa.parse(text.startsWith(BOM) ? text.slice(1) : text, {
    header: true,
    skipEmptyLines: true
  });
  const csv = Papa.unparse({
    fields: EXPORT_COLUMNS.map((col) => col.label),
    data: data.map((row) => EXPORT_COLUMNS.map((col) => renderCell(row[col.csv], col.render)))
  });
  return new Blob([BOM, csv], { type: 'text/csv;charset=utf-8' });
};

const AiChatReport = () => {
  const dispatch = useDispatch();
  const userRolePermission = useUserRolePermission();
  const { isDownloading, downloadFile } = useReportDownload();
  const timezone = useTimezone();
  const today = toZonedTime(new Date(), timezone);

  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({});
  // Filters of the last search, so the CSV matches the rows on screen.
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [botOptions, setBotOptions] = useState([]);
  const [dispositions, setDispositions] = useState([]);
  const [transcriptChatId, setTranscriptChatId] = useState(null);

  // The API returns only today's chats when no range is sent, so start on today.
  const initialValues = {
    page: 1,
    limit: 10,
    fromDate: today,
    toDate: today,
    bot_id: '',
    disposition_id: '',
    status: '',
    close_reason: '',
    search: ''
  };

  const validationSchema = Yup.object({
    fromDate: Yup.date().required('From date is required'),
    toDate: Yup.date().required('To date is required')
  });

  const onSubmit = async (valuesToUse) => {
    setIsLoading(true);
    setIsFilterOpen(false);
    try {
      const filters = {
        from_date: format(valuesToUse.fromDate, 'yyyy-MM-dd'),
        to_date: format(valuesToUse.toDate, 'yyyy-MM-dd')
      };
      ['bot_id', 'disposition_id', 'status', 'close_reason'].forEach((key) => {
        if (valuesToUse[key]) filters[key] = valuesToUse[key];
      });
      if (valuesToUse.search?.trim()) filters.search = valuesToUse.search.trim();
      setAppliedFilters(filters);

      const payload = { page: valuesToUse.page, limit: valuesToUse.limit, ...filters };
      const response = await dispatch(botChatReportGet(payload)).unwrap();
      setRows(Array.isArray(response?.data) ? response.data : []);
      setPagination({
        total: response?.total ?? 0,
        page: response?.page ?? valuesToUse.page,
        limit: response?.limit ?? valuesToUse.limit
      });
    } catch (error) {
      console.log('error', error);
      setRows([]);
      setPagination({});
    } finally {
      setIsLoading(false);
    }
  };

  const { values, handleChange, handleBlur, setFieldValue, handleSubmit, touched, errors } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit: (v) => onSubmit(v)
    });

  // Every matching chat (up to 50,000 rows) as CSV, built by the backend and reshaped to
  // the listing format.
  const handleExport = () => {
    if (!appliedFilters) return;
    downloadFile({
      name: EXPORT_NAME,
      count: pagination?.total,
      fileName: 'chat-report.csv',
      request: async (onDownloadProgress) =>
        toListingCsv(
          await dispatch(botChatReportExport({ ...appliedFilters, onDownloadProgress })).unwrap()
        )
    });
  };

  useEffect(() => {
    onSubmit(values);
  }, [values.limit, values.page]);

  const getBotOptions = async () => {
    try {
      const response = await dispatch(callexAiAgentOptionsGet()).unwrap();
      setBotOptions(
        response?.data ? response.data.map((val) => ({ label: val.name, value: val.id })) : []
      );
    } catch (error) {
      setBotOptions([]);
      console.log('error', error);
    }
  };

  const getDispositions = async () => {
    try {
      const response = await dispatch(aiDispositionListGet({ page: 1, limit: 1000 })).unwrap();
      setDispositions(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      setDispositions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getBotOptions();
    getDispositions();
  }, []);

  // Dispositions belong to a bot and the filter takes the id, so with no bot selected
  // each one carries its bot's name (every bot has its own "Voicemail").
  const dispositionOptions = useMemo(() => {
    const botNames = Object.fromEntries(botOptions.map((opt) => [String(opt.value), opt.label]));
    const options = dispositions
      .filter((item) => !values.bot_id || String(item.bot_id) === String(values.bot_id))
      .map((item) => ({
        label:
          values.bot_id || !botNames[String(item.bot_id)]
            ? item.name
            : `${item.name} (${botNames[String(item.bot_id)]})`,
        value: item.id
      }));
    return [{ label: 'All', value: '' }, ...options];
  }, [dispositions, botOptions, values.bot_id]);

  useEffect(() => {
    if (!dispositionOptions.some((opt) => opt.value === values.disposition_id)) {
      setFieldValue('disposition_id', '');
    }
  }, [dispositionOptions]);

  return (
    <div>
      <AdminHeader
        title="AI Chat Report"
        isSearch
        onSearchClick={() => setIsFilterOpen(!isFilterOpen)}
        isExportDisabled={!checkModulePermission('is_export', PERMISSION_KEY, userRolePermission)}
        isManualExport
        isExporting={isDownloading(EXPORT_NAME)}
        isExport={rows.length > 0}
        onExportClick={() => handleExport()}
      />

      {isFilterOpen && (
        <Card className="mb-3 p-3">
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  name="fromDate"
                  label="From Date"
                  dateFormat="yyyy-MM-dd"
                  value={values.fromDate}
                  maxDate={values.toDate || today}
                  onChange={(e) => setFieldValue('fromDate', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  name="toDate"
                  label="To Date"
                  dateFormat="yyyy-MM-dd"
                  value={values.toDate}
                  minDate={values.fromDate}
                  maxDate={today}
                  onChange={(e) => setFieldValue('toDate', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormSelect
                  name="bot_id"
                  label="Bot"
                  placeholder="Select Bot"
                  value={values.bot_id}
                  options={[{ label: 'All', value: '' }, ...botOptions]}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormSelect
                  name="disposition_id"
                  label="Disposition"
                  placeholder="Select Disposition"
                  value={values.disposition_id}
                  options={dispositionOptions}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormSelect
                  name="status"
                  label="Status"
                  placeholder="Select Status"
                  value={values.status}
                  options={CHAT_STATUS_OPTIONS}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormSelect
                  name="close_reason"
                  label="Close Reason"
                  placeholder="Select Close Reason"
                  value={values.close_reason}
                  options={CLOSE_REASON_OPTIONS}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormInput
                  name="search"
                  label="Search"
                  placeholder="Bot name or chat text"
                  value={values.search}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            </Row>
            <CardFooter className="p-0 bg-white text-end">
              <Button color="primary" className="px-4" type="submit">
                Search
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}

      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 410px)' : 'calc(100vh - 170px)' }}>
          <CallexLoader />
        </Card>
      ) : rows.length > 0 &&
        checkModulePermission('is_view', PERMISSION_KEY, userRolePermission) ? (
        <Card className="p-0 position-relative m-0">
          <div
            className="daily-report-table"
            style={{ height: isFilterOpen ? 'calc(100vh - 450px)' : 'calc(100vh - 210px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  {COLUMNS.map((col) => (
                    <th key={col.name}>{col.label}</th>
                  ))}
                  <th className="text-center" style={{ width: '100px' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row?.id || index}>
                    {COLUMNS.map((col) => (
                      <td key={col.name}>{renderCell(row?.[col.name], col.render)}</td>
                    ))}
                    <td className="text-center">
                      <Icon.FileText
                        size={16}
                        title="Transcript"
                        className="cursor-pointer text-primary"
                        onClick={() => setTranscriptChatId(row.id)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={pagination?.total || 0}
              limit={pagination?.limit || values.limit}
              page={pagination?.page || values.page}
              onLimitChange={(val) => {
                setFieldValue('limit', val);
                setFieldValue('page', 1);
              }}
              onPageClick={(val) => setFieldValue('page', val)}
              lastPageClick={(val) => setFieldValue('page', val)}
              firstPageClick={(val) => setFieldValue('page', val)}
              onRefreshClick={() => onSubmit(values)}
            />
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 410px)' : 'calc(100vh - 170px)' }}>
          No data available
        </Card>
      )}

      <AiChatTranscriptModal
        isOpen={!!transcriptChatId}
        chatId={transcriptChatId}
        onClose={() => setTranscriptChatId(null)}
      />
    </div>
  );
};

export default AiChatReport;
